// Core packages
import { CollectionViewer } from "@angular/cdk/collections";
import { DataSource } from "@angular/cdk/table";

// Third party packages
import { BehaviorSubject, Observable, of } from "rxjs";
import { catchError, finalize } from "rxjs/operators";

// Custom packages
import { PropertyRemappingsService } from "src/app/modules/properties/property-remappings.service";
import { HelperService } from "src/app/shared/services/helper.service";

/**
 * Material data source for the property remappings history table.
 *
 * @see statements-data-source.ts for the shared pattern.
 */
export class PropertyRemappingsDataSource implements DataSource<any> {
  private itemsSubject$ = new BehaviorSubject<any[]>([]);
  private totalCountSubject$ = new BehaviorSubject<number>(0);
  private loadingSubject$ = new BehaviorSubject<boolean>(true);

  public loading$ = this.loadingSubject$.asObservable();
  public totalCount$ = this.totalCountSubject$.asObservable();

  /**
   * Class constructor
   */
  constructor(
    private propertyRemappingsService: PropertyRemappingsService,
    private helperService: HelperService
  ) {}

  /**
   * Connect data table to itemsSubject$
   *
   * @since 3.0.0
   */
  connect(collectionViewer: CollectionViewer): Observable<any[]> {
    return this.itemsSubject$.asObservable();
  }

  /**
   * Disconnect data table from subjects
   *
   * @since 3.0.0
   */
  disconnect(collectionViewer: CollectionViewer): void {
    this.itemsSubject$.complete();
    this.totalCountSubject$.complete();
    this.loadingSubject$.complete();
  }

  /**
   * Load items from back-end using the injected service
   *
   * @since 3.0.0
   */
  loadItems(
    propertyId: string,
    start?: number,
    limit?: number,
    sort?: string,
    sortVersus?: string,
    search?: any
  ): void {
    this.loadingSubject$.next(true); // Start loading
    this.propertyRemappingsService
      .getList(propertyId, start, limit, sort, sortVersus, search)
      .pipe(
        catchError((err) => {
          // If errors, return empty array
          this.helperService.handleError(err);
          return of({ status: true, data: { items: [], totalCount: 0 } });
        }),
        finalize(() => this.loadingSubject$.next(false)) // Stop loading on finalize
      )
      .subscribe((result: any) => {
        this.itemsSubject$.next(result.data.items);
        this.totalCountSubject$.next(result.data.totalCount);
      });
  }
}
